import type { ReactElement } from "react";
import { Link, useLocation, useSearchParams } from "react-router";

import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { totpRules } from "../../forms/view-model-rules.js";

import { accountApi } from "./urls.js";

/**
 * メールによる二段階認証（認証アプリが使えないときの予備。移行元と同じ。
 * Implem.Portal.Backend の docs/features/02）。
 *
 * <b>戻り先と「ログインしたままにする」は URL で受け取り、隠し欄で送り返す</b>
 * （確認コードの画面と同じ。欄の名前は API が受ける ViewModel と同じ）。
 */

/** ログインの途中の状態を運ぶ隠し欄。 */
function SignInStateFields(): ReactElement {
    const [search] = useSearchParams();
    return (
        <>
            <input type="hidden" name="ReturnUrl" value={queryValue(search, "returnUrl") ?? ""} />
            <input type="hidden" name="RememberMe" value={queryValue(search, "rememberMe") ?? "false"} />
        </>
    );
}

/** 認証アプリの確認コードの画面へ、ログインの途中の状態を付けて戻る URL。 */
const verifyTotpUrlOf = (search: URLSearchParams): string => {
    const query = new URLSearchParams();
    const returnUrl = queryValue(search, "returnUrl");
    if (returnUrl !== undefined && returnUrl !== "") {
        query.set("returnUrl", returnUrl);
    }
    query.set("rememberMe", queryValue(search, "rememberMe") ?? "False");
    return `/Account/VerifyTotpLogin?${query.toString()}`;
};

/**
 * 「メールで確認コードを受け取る」（認証アプリの登録と確認コードの画面の下に置く）。
 *
 * <b>移る先は API が返す</b>（コードの入力の画面）。中間 Cookie が切れていれば 401 で、ログインからやり直す。
 */
export function EmailCodeOffer({ lead }: { readonly lead: string }): ReactElement {
    const form = useApiForm(accountApi.sendEmailCode);

    return (
        <form method="post" noValidate onSubmit={form.onSubmit} className="panel-footer text-center" data-email-code-offer="">
            <FormSummary messages={form.summary} />
            <SignInStateFields />
            <p className="mb-2">{lead}</p>
            <button type="submit" className="btn-quiet btn-sm" disabled={form.busy}>
                メールで確認コードを受け取る
            </button>
        </form>
    );
}

/**
 * メールで認証（ログインの 2 段目の予備）。
 *
 * <b>開くたびに描き直す。</b>「コードをもう一度送る」は同じ画面へ移り直す（通知を出す）が、
 * 同じ画面のままだと送っている間の状態が残り、ボタンを押せないままになる。
 */
export function VerifyEmailCodePage(): ReactElement {
    const { key } = useLocation();
    return <VerifyEmailCodeForms key={key} />;
}

function VerifyEmailCodeForms(): ReactElement {
    usePageMeta("メールで認証");
    const form = useApiForm(accountApi.verifyEmailCode, totpRules);
    const resend = useApiForm(accountApi.sendEmailCode);
    const [search] = useSearchParams();

    return (
        <div className="page max-w-md">
            <div className="panel">
                <h1 className="page-title">メールで認証</h1>
                <p className="page-lead">
                    ご登録のメールアドレスに確認コードをお送りしました。
                    メールに記載の 6 桁のコードを入力してください。コードの有効期限はおよそ 10 分です。
                </p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />
                    <SignInStateFields />

                    <TextField
                        label="確認コード" name="Code" required
                        inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                        placeholder="000000" autoFocus
                        wrapperClassName="mb-7"
                        inputClassName="text-center text-2xl tracking-[0.4em]"
                        error={form.fieldError("Code")}
                    />

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>
                        ログイン
                    </button>
                </form>

                <form method="post" noValidate onSubmit={resend.onSubmit} className="mt-4" data-email-code-resend="">
                    <FormSummary messages={resend.summary} />
                    <SignInStateFields />
                    <button type="submit" className="btn-secondary w-full" disabled={resend.busy}>
                        コードをもう一度送る
                    </button>
                </form>

                <div className="panel-footer space-y-3">
                    <p>
                        <Link to={verifyTotpUrlOf(search)} className="btn-secondary btn-sm">認証アプリで認証する</Link>
                    </p>
                    <p className="text-xs">
                        メールが届かない場合は、迷惑メールフォルダをご確認ください。認証アプリを使う方が安全です。
                    </p>
                </div>
            </div>
        </div>
    );
}
